import { useContext, useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useMutation, useQuery } from '@tanstack/react-query';
// Components
import {
	Button,
	ClientRemove,
	FormSkeleton,
	Modal,
	PhoneFieldFormik,
	RadioField,
	Separator,
	Status,
	TextFieldFormik,
	Tooltip,
} from '@components';
import { CONSTANT } from '@const';
import { ModalContext } from '@context';
import { ClientService } from '@data';
import { Close, Done, Edit } from '@public';
import {
	AddressSearchFormValues,
	AddressType,
	ClientContact,
	ClientContactFormValues,
	ClientDetailsFormValues,
	ClientType,
} from '@types';
import {
	alternatePhoneRegExp,
	aussieMobilePhoneRegExp,
	emailRegExp,
	FormEditability,
	FormLabels,
	getEmptyClientContactFormValues,
	getEmptyClientDetailsFormValues,
	isDirtyFormOnTrim,
	mapAddress,
	mapClientContactFormValuesToModel,
	mapClientContactToFormValues,
	mapClientToFormValues,
	toSentenceCase,
} from '@utils';
import { FieldArray, Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { ApiError, ApiService } from '@/services/api/ApiService';

import modalStyles from './../../modals/modals.module.scss';
// Styles
import styles from './clientDetails.module.scss';

const clientDetailsFormLabels: FormLabels<ClientDetailsFormValues> = {
	clientName: 'Client name',
	clientReference: 'Client reference',
	mailingName: 'Mailing name',
	legalName: 'Legal name',
	streetAddress: 'Street address',
	postalAddress: 'Postal address',
	primaryContact: 'Primary contact',
};

const contactDetailsFormLabels: FormLabels<ClientContactFormValues> = {
	id: 'Id',
	firstName: 'First name',
	middleName: 'Middle name',
	lastName: 'Last name',
	preferredContactPhone: 'Preferred phone',
	mobile: 'Mobile phone',
	alternatePhone: 'Alternate phone',
	streetAddress: 'Street address',
	postalAddress: 'Postal address',
	emailAddress: 'Email address',
	emailVerified: '',
	roles: 'Role/s',
};

const contactDetailsValidationSchema = Yup.object().shape({
	firstName: Yup.string().nullable().optional(),
	middleName: Yup.string().nullable().optional(),
	lastName: Yup.string().nullable().optional(),
	alternatePhone: Yup.string().when('preferredContactPhone', {
		is: 'AlternatePhone',
		then: schema =>
			schema
				.required('Alternate phone is required')
				.matches(alternatePhoneRegExp, 'Alternate phone must be empty or between 8 and 18 digits'),
		otherwise: schema =>
			schema.nullable().optional().matches(alternatePhoneRegExp, {
				message: 'Alternate phone must be empty or between 8 and 18 digits',
				excludeEmptyString: true,
			}),
	}),
	mobile: Yup.string()
		.matches(aussieMobilePhoneRegExp, 'Invalid mobile phone number')
		.when('preferredContactPhone', {
			is: 'Mobile',
			then: schema => schema.required('Mobile phone is required'),
			otherwise: schema => schema.nullable().optional(),
		}),
	preferredContactPhone: Yup.string().required('Please choose a preferred contact phone number'),
	streetAddress: Yup.object()
		.shape({
			postalCode: Yup.string().required('Street address is required'),
		})
		.required(),
	postalAddress: Yup.object()
		.shape({
			postalCode: Yup.string().required('Postal address is required'),
		})
		.required(),
	emailAddress: Yup.string()
		.matches(emailRegExp, 'Email address is not valid')
		.required('Email address is required'),
	emailVerified: Yup.boolean().isTrue('Email address has to be verified'),
});

const formValuesEditability: FormEditability<ClientContactFormValues> = {
	firstName: false,
	middleName: false,
	lastName: false,
	preferredContactPhone: true,
	mobile: true,
	alternatePhone: true,
	streetAddress: true,
	postalAddress: true,
	emailAddress: true,
	emailVerified: false,
	roles: false,
};

interface ClientDetailsProps {
	client: ClientType;
	onClickBack?: () => void;
}

export const ClientDetails = ({
	client,
	onClickBack = () => {
		return;
	},
}: ClientDetailsProps) => {
	// Context
	const {
		setShowAddressModal,
		addressTypeModal,
		setAddressTypeModal,
		setStreetAddress: setStreetAddressModal,
		setPostalAddress: setPostalAddressModal,
		addressFormValuesOnSave,
		setAddressFormValuesOnSave,
		emailAddressFromModal,
		setEmailAddressFromModal,
		setEmailAddress: setEmailAddressModal,
		setShowEmailAddressModal,
	} = useContext(ModalContext);

	// State;
	const [isEditing, setIsEditing] = useState(false);
	const [hasSetDataFromModal, setHasSetDataFromModal] = useState(false);
	const [clientDetailsInitialValues, setClientDetailsInitialValues] =
		useState<ClientDetailsFormValues>(getEmptyClientDetailsFormValues());
	const [clientContactOriginalValues, setClientContactOriginalValues] =
		useState<ClientContactFormValues>(getEmptyClientContactFormValues());
	const [clientContactInitialValues, setClientContactInitialValues] =
		useState<ClientContactFormValues>(getEmptyClientContactFormValues());
	const [showSuccess, setShowSuccess] = useState(false);
	const [editErrorMessage, setEditErrorMessage] = useState<string>('');
	const contactDetailsFormikRef = useRef<FormikProps<ClientContactFormValues>>(null);
	const [showRemoveClient, setRemoveClient] = useState<boolean>(false);

	// Data
	const getClientDetails = useQuery({
		queryKey: [CONSTANT.LEGAL_ENTITY_API_URL, client.id],
		queryFn: () => ClientService.getClient(client.id),
	});

	// Initialize form values when client details are fetched
	useEffect(() => {
		if (getClientDetails.data) {
			setClientDetailsInitialValues(mapClientToFormValues(getClientDetails.data));
			const nextClientContactValues = mapClientContactToFormValues(
				getClientDetails.data.contactToClient,
			);
			setClientContactOriginalValues({ ...nextClientContactValues });
			setClientContactInitialValues({ ...nextClientContactValues });
		}
	}, [getClientDetails.data]);

	const updateClient = useMutation({
		mutationFn: (partialClient: Partial<ClientType>) =>
			ClientService.updateClientContact(client.id, partialClient),
		onSuccess: async () => await getClientDetails.refetch(),
	});

	// Helper
	const submitClientContactForm = async (values: ClientContactFormValues) => {
		setClientContactInitialValues(values); // keep inputed values on form while submitting

		const clientContactUpdates: Partial<ClientContact> = mapClientContactFormValuesToModel(
			values,
			formValuesEditability,
		);

		await updateClient
			.mutateAsync(clientContactUpdates)
			.then(() => {
				setShowSuccess(true);
				setIsEditing(false);
			})
			.catch((error: ApiError) => {
				const errorMessage = ApiService.getErrorMessage(error);
				if (ApiService.getErrorStatusCode(error) === 400 && errorMessage)
					setEditErrorMessage(toSentenceCase(errorMessage));
				else setEditErrorMessage('Submission failed due to server error. Please try again later.');
			});
	};

	const isEditable = (field: string) => {
		return formValuesEditability[field as keyof ClientContactFormValues] ?? false;
	};

	const activateForm = () => {
		setIsEditing(true);
		setHasSetDataFromModal(false);
		setShowSuccess(false);
		setEditErrorMessage('');
		updateClient.reset();
	};

	const cancelForm = () => {
		setIsEditing(false);
		setEditErrorMessage('');
		setClientContactInitialValues({ ...clientContactOriginalValues });
		updateClient.reset();
	};

	const openAddressModal = async (addressType: AddressType, values: ClientContactFormValues) => {
		setAddressTypeModal(addressType);
		setStreetAddressModal(values.streetAddress);
		setPostalAddressModal(values.postalAddress);
		setShowAddressModal(true);

		setClientContactInitialValues(values); // keep inputed values on form while submitting
	};

	const openEmailAddressModal = async (values: ClientContactFormValues) => {
		setEmailAddressModal(values.emailAddress);
		setShowEmailAddressModal(true);

		setClientContactInitialValues(values); // keep inputed values on form while submitting
	};

	// Life cycle
	// Hide modal if was shown on a previous navigation
	useEffect(() => {
		setShowAddressModal(false);
	}, [setShowAddressModal]);

	useEffect(() => {
		setShowEmailAddressModal(false); // hide modal if was shown on a previous navigation
	}, [setShowEmailAddressModal]);

	// Set child form (from address modal) on parent form
	useEffect(() => {
		const onSaveAddressModal = (addressSearchForm: AddressSearchFormValues) => {
			if (contactDetailsFormikRef.current && addressSearchForm.address) {
				if (addressTypeModal == AddressType.STREET) {
					contactDetailsFormikRef.current.values.streetAddress = addressSearchForm.address; // set streetAddress on form
				}
				if (
					addressSearchForm.isOverwrittingPostalAddress ||
					addressTypeModal == AddressType.POSTAL
				) {
					contactDetailsFormikRef.current.values.postalAddress = addressSearchForm.address; // set postalAddress on form
				}

				// update values on form for rendering it again with them
				setClientContactInitialValues({
					...clientContactInitialValues,
					streetAddress: contactDetailsFormikRef.current.values.streetAddress,
					postalAddress: contactDetailsFormikRef.current.values.postalAddress,
				});
				setHasSetDataFromModal(true); // make the form dirty
			}
		};

		if (addressFormValuesOnSave) {
			const values = { ...addressFormValuesOnSave };
			setAddressFormValuesOnSave(undefined);
			onSaveAddressModal(values);
		}
	}, [
		addressFormValuesOnSave,
		addressTypeModal,
		clientContactInitialValues,
		setAddressFormValuesOnSave,
	]);

	// Set child data (from email address modal) on parent form
	useEffect(() => {
		if (emailAddressFromModal) {
			const tempEmail = emailAddressFromModal;
			setEmailAddressFromModal(undefined);
			// update values on form for rendering it again with them
			setClientContactInitialValues({
				...clientContactInitialValues,
				emailAddress: tempEmail,
				emailVerified: true,
			});
			setHasSetDataFromModal(true); // make the form dirty
		}
	}, [emailAddressFromModal, setEmailAddressFromModal, clientContactInitialValues]);

	const EmailAddressEditIcon = ({
		values,
		hasError,
	}: {
		values: ClientContactFormValues;
		hasError?: boolean;
	}) => {
		return (
			<Edit
				customStyles={[styles['edit-icon'], styles[`edit-icon-${hasError ? 'error' : ''}`]].join(
					' ',
				)}
				handleClick={() => openEmailAddressModal(values)}
			/>
		);
	};

	const AddressEditIcon = ({
		addressType,
		values,
		hasError,
	}: {
		addressType: AddressType;
		values: ClientContactFormValues;
		hasError?: boolean;
	}) => {
		return (
			<Edit
				customStyles={[styles['edit-icon'], styles[`edit-icon-${hasError ? 'error' : ''}`]].join(
					' ',
				)}
				handleClick={() => openAddressModal(addressType, values)}
			/>
		);
	};

	const RolesTooltip = () => {
		return (
			<Tooltip
				title={'Updating client details'}
				text={`Some contact details are unable to be updated using WaterIQ at this time. 
				Please use the 'Contact us' functionality to update these details.`}
				id="roles-tooltip"
			/>
		);
	};

	const MobileTooltip = () => {
		return (
			<Tooltip
				title={'Mobile phone'}
				text={`The mobile phone number should be an Australian mobile number. 
					   If you have an international contact number, please enter it in the 'Alternate phone' field.`}
				id="mobile-tooltip"
			/>
		);
	};

	const StatusComponent = () => {
		return showSuccess || editErrorMessage ? (
			<div className={styles.status}>
				<Status
					status={showSuccess ? 'success' : 'error'}
					show={true}
					onClose={() => {
						setShowSuccess(false);
						setEditErrorMessage('');
					}}
					title={showSuccess ? 'Updated details have been successfully saved.' : undefined}
					msg={
						showSuccess
							? 'A water officer may contact you to verify these details before they are processed. It may take up to 15 business days for updated details to be processed.'
							: editErrorMessage
					}
				/>
			</div>
		) : (
			<></>
		);
	};

	const modalRemoveClient = () => (
		<Modal
			customModalStyle={modalStyles['modal-client']}
			customBackdropStyle={modalStyles['backdrop']}
			open={showRemoveClient}
			closeable
			handleClose={() => setRemoveClient(false)}
		>
			<ClientRemove clientId={client.reference} onClickBack={onClickBack} />
		</Modal>
	);

	const ClientDetailsForm = () => (
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		<Formik initialValues={clientDetailsInitialValues} onSubmit={() => {}}>
			{({}: FormikProps<ClientDetailsFormValues>, isDisabled = true) => (
				<Form>
					{getClientDetails.isLoading ? (
						<FormSkeleton labels={clientDetailsFormLabels} />
					) : (
						<div className="form--grid">
							<>
								<label>{clientDetailsFormLabels.clientName}:</label>
								<TextFieldFormik
									customStyles={styles['form-field']}
									props={{
										name: 'clientName',
										disabled: isDisabled,
									}}
								/>
								<p></p>
							</>
							<>
								<label>{clientDetailsFormLabels.clientReference}:</label>
								<TextFieldFormik
									customStyles={styles['form-field']}
									props={{
										name: 'clientReference',
										disabled: isDisabled,
									}}
									size="small"
								/>
								<p></p>
							</>
							<>
								<label>{clientDetailsFormLabels.mailingName}:</label>
								<TextFieldFormik
									customStyles={styles['form-field']}
									props={{
										name: 'mailingName',
										disabled: isDisabled,
									}}
								/>
								<p></p>
							</>
							<>
								<label>{clientDetailsFormLabels.legalName}:</label>
								<TextFieldFormik
									customStyles={styles['form-field']}
									props={{
										name: 'legalName',
										disabled: isDisabled,
									}}
								/>
								<p></p>
							</>
							<>
								<label>{clientDetailsFormLabels.streetAddress}:</label>
								<TextFieldFormik
									customStyles={styles['form-field']}
									props={{
										name: 'streetAddress',
										disabled: isDisabled,
									}}
								/>
								<p></p>
							</>
							<>
								<label>{clientDetailsFormLabels.postalAddress}:</label>
								<TextFieldFormik
									customStyles={styles['form-field']}
									props={{
										name: 'postalAddress',
										disabled: isDisabled,
									}}
								/>
								<p></p>
							</>
							<>
								<label>{clientDetailsFormLabels.primaryContact}:</label>
								<TextFieldFormik
									customStyles={styles['form-field']}
									props={{
										name: 'primaryContact',
										disabled: isDisabled,
									}}
								/>
								<p></p>
							</>
						</div>
					)}
				</Form>
			)}
		</Formik>
	);

	const ContactDetailsForm = () => (
		<>
			<Formik
				initialValues={clientContactInitialValues}
				validationSchema={contactDetailsValidationSchema}
				onSubmit={submitClientContactForm}
				innerRef={contactDetailsFormikRef}
				validateOnChange={true}
			>
				{(
					{
						dirty,
						submitForm,
						values,
						errors,
						touched,
						getFieldHelpers,
					}: FormikProps<ClientContactFormValues>,
					isDisabled = !isEditing || updateClient.isPending,
				) => (
					<Form>
						{getClientDetails.isLoading ? (
							<FormSkeleton labels={contactDetailsFormLabels} />
						) : (
							<div
								className={['form--grid', updateClient.isPending ? 'form--frozen' : ''].join(' ')}
							>
								<>
									<label>{contactDetailsFormLabels.firstName}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'firstName',
											placeholder: 'E.g. Mark',
											disabled: isDisabled || !isEditable('firstName'),
										}}
										size="small"
									/>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.middleName}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'middleName',
											placeholder: 'E.g. John',
											disabled: isDisabled || !isEditable('middleName'),
										}}
										size="small"
									/>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.lastName}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'lastName',
											placeholder: 'E.g. Scott',
											disabled: isDisabled || !isEditable('lastName'),
										}}
										size="small"
									/>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.mobile}:</label>
									<div className={styles['radio-buttons-container']}>
										<PhoneFieldFormik
											customStyles={styles['form-field']}
											defaultCountry="au"
											forceCountry={true}
											props={{
												name: 'mobile',
												disabled: isDisabled || !isEditable('mobile'),
												hidden: isDisabled && values.mobile == '',
											}}
										/>
										<div className={styles['radio-buttons-padding']}></div>
										<RadioField
											value="Mobile"
											hidden={isDisabled && values.preferredContactPhone != 'Mobile'}
											isChecked={values.preferredContactPhone == 'Mobile'}
											disabled={isDisabled || !isEditable('preferredContactPhone')}
											onChange={(value: string) =>
												getFieldHelpers('preferredContactPhone').setValue(value)
											}
											customStyles={[styles.cell, styles['cell-half-left'], styles.radioField].join(
												' ',
											)}
										>
											Preferred
										</RadioField>
									</div>
									{isEditing ? <MobileTooltip /> : <p></p>}
								</>
								<>
									<label>{contactDetailsFormLabels.alternatePhone}:</label>
									<div className={styles['radio-buttons-container']}>
										<PhoneFieldFormik
											customStyles={styles['form-field']}
											defaultCountry="au"
											props={{
												name: 'alternatePhone',
												disabled: isDisabled || !isEditable('alternatePhone'),
												hidden: isDisabled && values.alternatePhone == '',
											}}
										/>

										<div className={styles['radio-buttons-padding']}></div>
										<RadioField
											value="AlternatePhone"
											isChecked={values.preferredContactPhone == 'AlternatePhone'}
											hidden={isDisabled && values.preferredContactPhone != 'AlternatePhone'}
											disabled={isDisabled || !isEditable('preferredContactPhone')}
											onChange={(value: string) =>
												getFieldHelpers('preferredContactPhone').setValue(value)
											}
											customStyles={[styles.cell, styles['cell-half-left'], styles.radioField].join(
												' ',
											)}
										>
											Preferred
										</RadioField>
									</div>
									<p></p>
								</>
								<>
									<label>{contactDetailsFormLabels.streetAddress}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'streetAddress.postalCode',
											disabled: true,
										}}
										isLocked={true}
										onLockedText={mapAddress(values.streetAddress)}
									/>
									{isEditing ? (
										<AddressEditIcon
											addressType={AddressType.STREET}
											values={values}
											hasError={Boolean(
												// eslint-disable-next-line
												// @ts-ignore // Formik detects type as string instead of AddressType
												errors.streetAddress?.postalCode && touched.streetAddress?.postalCode,
											)}
										/>
									) : (
										<p></p>
									)}
								</>
								<>
									<label>{contactDetailsFormLabels.postalAddress}:</label>
									<TextFieldFormik
										customStyles={styles['form-field']}
										props={{
											name: 'postalAddress.postalCode',
											disabled: true,
										}}
										isLocked={true}
										onLockedText={mapAddress(values.postalAddress)}
									/>
									{isEditing ? (
										<AddressEditIcon
											addressType={AddressType.POSTAL}
											values={values}
											hasError={Boolean(
												// eslint-disable-next-line
												// @ts-ignore // Formik detects type as string instead of AddressType
												errors.postalAddress?.postalCode && touched.postalAddress?.postalCode,
											)}
										/>
									) : (
										<p></p>
									)}
								</>
								<>
									<label>{contactDetailsFormLabels.emailAddress}:</label>
									<div className={styles['text-row']}>
										<div className={[styles.text, styles['text-width']].join(' ')}>
											{values.emailAddress}
										</div>
										<div>
											{values.emailVerified ? (
												<div className={[styles.text, styles['text-verified']].join(' ')}>
													<Done customStyles={[styles.icon, styles['icon-verified']].join(' ')} />
													Verified
												</div>
											) : (
												<div className={[styles.text, styles['text-not-verified']].join(' ')}>
													<Close
														customStyles={[styles.icon, styles['icon-not-verified']].join(' ')}
													/>
													Not verified
												</div>
											)}
										</div>
									</div>
									{isEditing ? (
										<EmailAddressEditIcon
											values={values}
											hasError={Boolean(
												(errors.emailAddress && touched.emailAddress) ||
													(errors.emailVerified && touched.emailVerified),
											)}
										/>
									) : (
										<p></p>
									)}
								</>
								<>
									<label>{contactDetailsFormLabels.roles}:</label>
									{values.roles && values.roles.length > 0 ? (
										<FieldArray
											name="roles"
											render={() => (
												<>
													{values.roles.map((role, index) => (
														<TextFieldFormik
															props={{
																name: `roles.${index}`,
																disabled: isDisabled || !isEditable('roles'),
															}}
															key={index}
															customStyles={styles.arrayTextField}
														/>
													))}
												</>
											)}
										/>
									) : (
										<p></p>
									)}
									{isEditing ? <RolesTooltip /> : <p></p>}
								</>
							</div>
						)}
						<StatusComponent />
						<NavigationButtons
							isDirty={isDirtyFormOnTrim(dirty, clientContactInitialValues, values)}
							submitForm={submitForm}
						/>
					</Form>
				)}
			</Formik>
		</>
	);

	const NavigationButtons = ({
		isDirty,
		submitForm,
	}: {
		isDirty: boolean;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		submitForm: (() => Promise<void>) & (() => Promise<any>);
	}) => (
		<div className={styles['buttons-container']}>
			<Button
				key={'back'}
				type={'secondary'}
				size="large"
				onClick={onClickBack}
				disabled={updateClient.isPending}
				customStyles={styles.navButton}
				data-testid="back"
			>
				Back to account details
			</Button>
			<div className={styles['buttons-container-row']}>
				{isEditing && (
					<Button
						customStyles={[styles['cancel-button'], styles.navButton].join(' ')}
						key="cancel"
						type="secondary"
						size="small"
						onClick={cancelForm}
						disabled={updateClient.isPending}
						data-testid="cancel"
					>
						Cancel
					</Button>
				)}

				<Button
					key={'update'}
					type="primary"
					size="large"
					onClick={() => {
						if (!isEditing) {
							activateForm();
						} else {
							submitForm();
						}
					}}
					isLoading={updateClient.isPending}
					disabled={
						(!isEditing && getClientDetails.isLoading) ||
						(isEditing &&
							!isDirty &&
							!hasSetDataFromModal &&
							!updateClient.isPending &&
							!updateClient.isError)
					}
					customStyles={styles.navButton}
					data-testid="update"
				>
					{!isEditing ? 'Edit contact details' : 'Save contact details'}
				</Button>
			</div>
		</div>
	);

	return (
		<>
			<Head>
				<title>WaterIQ - Client Details</title>
				<meta name="description" content="WaterIQ - Client Details" />
			</Head>
			<main>
				<div className={styles['title-container']}>
					<h1 className={styles.title} data-testid="pageTitle">
						Client details
					</h1>
					{isEditing && (
						<Tooltip
							customStyles={styles['title-tooltip']}
							title={'Updating client details'}
							text={`Some contact details are unable to be updated using WaterIQ at this time. 
										Please use the 'Contact us' functionality to update any details.`}
							id="title-tooltip"
						/>
					)}
				</div>
				<div className={styles.main}>
					<div className={styles['content-container']}>
						<div className={styles.content}>
							<ClientDetailsForm />

							<div className={[styles['section-title'], styles['header-with-button']].join(' ')}>
								<p>Contact details for {client.name}</p>
								<Button
									key={'back'}
									type={'secondary'}
									size="large"
									onClick={() => {
										setRemoveClient(true);
									}}
									disabled={updateClient.isPending}
									customStyles={styles.navButton}
									data-testid="remove"
								>
									Unlink Client
								</Button>
							</div>
							<Separator />
							<ContactDetailsForm />
						</div>
					</div>
				</div>
			</main>
			{showRemoveClient && modalRemoveClient()}
		</>
	);
};
